/**
 * One store, on its own terms.
 *
 * A store is not just a row inside a clinic: it has its own staff, its own
 * approved facts, and settings that either inherit from the clinic or override
 * it. That inheritance is shown explicitly, because "empty" and "inherited"
 * look identical otherwise and the difference decides what the AI may write.
 */
import { ThemePicker } from '../components/ThemePicker.tsx';
import { ROLE_LABEL } from '../roles.ts';
import { useState } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router';
import type { AdminStoreDetail } from '../api.ts';
import { ClinicFacts, type ClinicFactsData } from '../components/ClinicFacts.tsx';
import { del, patch } from '../api.ts';
import { DangerButton } from '../components/DangerButton.tsx';
import {
  Card,
  Empty,
  Pill,
  RowLink,
  Stat,
  StatusPill,
  Table,
  jaDate,
  jaNumber,
} from '../components/ui.tsx';
import { EditPanel, Field } from '../components/EditPanel.tsx';
import { ReadFromSite } from '../components/ReadFromSite.tsx';
import { InheritedKeywords, KeywordOverride } from '../components/KeywordOverride.tsx';
import {
  StoreProfileFields,
  storeProfileFrom,
  storeProfilePayload,
} from '../components/ProfileFields.tsx';
import { AddMember } from '../components/AddMember.tsx';
import { MemberEditor } from '../components/MemberEditor.tsx';
import { Select } from '../components/Select.tsx';

const FACT_LABEL: Record<string, string> = {
  treatments: '施術・コース・料金',
  personas: 'ペルソナ',
  supervisors: '監修医',
  cta_templates: 'CTAテンプレート',
  strengths: 'クリニックの強み',
};

/**
 * A store setting that is NULL inherits the clinic's value. Showing the
 * inherited value greyed, rather than an empty cell, is the whole point.
 */
/**
 * One registered value, or a dash.
 *
 * A dash rather than an empty cell for anything unset: an empty cell reads as a
 * rendering fault, and 「未登録」 repeated down a column reads as an error list.
 * The same row the clinic page uses, so the two screens describe a record the
 * same way.
 */
function StoreFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="factgrid__row">
      <dt>{label}</dt>
      <dd>{children || <span className="muted">—</span>}</dd>
    </div>
  );
}

function Inherited({ own, inherited }: { own: string | null; inherited: string | null }) {
  if (own) return <>{own}</>;
  if (inherited) {
    return (
      <span className="muted">
        {inherited} <Pill tone="muted">クリニックから継承</Pill>
      </span>
    );
  }
  return <span className="muted">未設定</span>;
}

/** The store profile fields a page can fill, named as the form names them. */
const STORE_SITE_LABELS: Record<string, string> = {
  postalCode: '郵便番号',
  prefecture: '都道府県',
  city: '市区町村',
  address: '住所',
  phone: '電話番号',
  accessNote: 'アクセス',
  hoursNote: '診療時間',
  closedNote: '休診日',
  reservationUrl: '予約URL',
  mapUrl: '地図URL',
};

export default function AdminStoreDetailPage() {
  const navigate = useNavigate();
  /*
   * Which page to read this store out of.
   *
   * Defaulted to whatever the store or its clinic has registered, and editable,
   * because a branch's hours and address are usually on its OWN page - a clinic
   * with three 院 has three アクセス pages and one root. Defaulting to the root
   * and letting it be corrected is better than either asking every time or
   * guessing a path that does not exist.
   */
  const [readUrl, setReadUrl] = useState('');

  const { store, members, facts, activity, material, siblingStores } = useLoaderData<
    AdminStoreDetail & {
      material: ClinicFactsData;
      siblingStores: { items: Array<{ id: string; name: string }> };
    }
  >();
  const totalFacts = facts.reduce((sum, fact) => sum + Number(fact.total), 0);

  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [form, setForm] = useState({
    name: store.name,
    status: store.status,
    siteUrl: store.site_url ?? '',
    defaultTone: store.default_tone ?? '',
    wordpressCategory: store.wordpress_category ?? '',
    // The one field whose empty value is not the same as its absent value.
    defaultKeywords: store.default_keywords,
  });
  const [profile, setProfile] = useState(() => storeProfileFrom(store));

  // Empty means "inherit from the clinic", so it is sent as null rather than
  // as an empty string — the two are different states in the database, and the
  // detail panel above renders them differently.
  const save = () =>
    patch(`/admin/stores/${store.id}`, {
      name: form.name,
      status: form.status,
      siteUrl: form.siteUrl ? form.siteUrl : null,
      defaultTone: form.defaultTone ? form.defaultTone : null,
      wordpressCategory: form.wordpressCategory ? form.wordpressCategory : null,
      defaultKeywords: form.defaultKeywords,
      ...storeProfilePayload(profile),
    });

  return (
    <>
      <Card
        title={store.name}
        caption={`${store.clinic_name} の店舗`}
        actions={
          <>
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => {
                setForm({
                  name: store.name,
                  status: store.status,
                  siteUrl: store.site_url ?? '',
                  defaultTone: store.default_tone ?? '',
                  wordpressCategory: store.wordpress_category ?? '',
                  defaultKeywords: store.default_keywords,
                });
                setProfile(storeProfileFrom(store));
                setEditing((open) => !open);
              }}
            >
              {editing ? '編集を閉じる' : '編集'}
            </button>
            <Link className="btn btn--ghost btn--sm" to={`/admin/clinics/${store.clinic_id}`}>
              クリニックを見る
            </Link>
          </>
        }
      >
        <EditPanel open={editing} onClose={() => setEditing(false)} onSubmit={save}>
          <Field label="店舗名" htmlFor="s-name" required>
            <input
              id="s-name"
              required
              maxLength={200}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field label="ステータス" htmlFor="s-status">
            <Select
              id="s-status"
              value={form.status}
              onChange={(status) => setForm({ ...form, status })}
              options={[
                { value: 'ACTIVE', label: '稼働中' },
                { value: 'SUSPENDED', label: '利用停止' },
                { value: 'ARCHIVED', label: 'アーカイブ' },
              ]}
            />
          </Field>
          <Field
            label="サイトURL（上書き）"
            htmlFor="s-url"
            hint="空欄にするとクリニックの値を継承します。"
          >
            <input
              id="s-url"
              type="url"
              placeholder={store.clinic_site_url ?? 'https://'}
              value={form.siteUrl}
              onChange={(e) => setForm({ ...form, siteUrl: e.target.value })}
            />
          </Field>
          <Field
            label="文体・トーン（上書き）"
            htmlFor="s-tone"
            hint="空欄にするとクリニックの値を継承します。"
          >
            <input
              id="s-tone"
              maxLength={40}
              placeholder={store.clinic_default_tone ?? ''}
              value={form.defaultTone}
              onChange={(e) => setForm({ ...form, defaultTone: e.target.value })}
            />
          </Field>
          <Field
            label="対策キーワード（上書き）"
            htmlFor="s-keywords"
            span="full"
            hint="この店舗の記事に、記事ごとのキーワードと合わせて渡されます。"
          >
            <KeywordOverride
              id="s-keywords"
              value={form.defaultKeywords}
              inherited={store.clinic_default_keywords}
              onChange={(defaultKeywords) => setForm({ ...form, defaultKeywords })}
            />
          </Field>
          <Field
            label="WordPressカテゴリ（上書き）"
            htmlFor="s-wp"
            hint="空欄にするとクリニックの値を継承します。"
          >
            <input
              id="s-wp"
              maxLength={120}
              placeholder={store.clinic_wordpress_category ?? ''}
              value={form.wordpressCategory}
              onChange={(e) => setForm({ ...form, wordpressCategory: e.target.value })}
            />
          </Field>
          <Field
            label="読み取り元のURL"
            htmlFor="s-read-url"
            span="wide"
            hint="この店舗のページ（アクセス・診療時間が載っているページ）を指定すると、下の項目を読み取れます。"
          >
            <input
              id="s-read-url"
              type="url"
              inputMode="url"
              maxLength={2000}
              value={readUrl || store.site_url || store.clinic_site_url || ''}
              placeholder="https://example.clinic/umeda/"
              onChange={(e) => setReadUrl(e.target.value)}
            />
          </Field>
          <ReadFromSite
            kind="store"
            url={readUrl || store.site_url || store.clinic_site_url || ''}
            labels={STORE_SITE_LABELS}
            onFilled={(fields) =>
              setProfile((current) => ({
                ...current,
                // Only the keys this form holds, and only the ones that came
                // back - a blank must not wipe something already entered.
                ...Object.fromEntries(
                  Object.entries(fields).filter(
                    ([key, value]) => value !== '' && key in current,
                  ),
                ),
              }))
            }
          />
          <StoreProfileFields idPrefix="s" value={profile} onChange={setProfile} />
        </EditPanel>

        <div className="grid grid--4">
          <Stat label="店舗ステータス" value={<StatusPill status={store.status} />} small />
          <Stat
            label="クリニック"
            value={store.clinic_name}
            small
            hint={<StatusPill status={store.clinic_status} />}
          />
          <Stat
            label="契約プラン"
            value={store.plan_id ?? '未設定'}
            small
            hint={<span className="muted small">クリニック単位の契約です</span>}
          />
          <Stat label="登録日" value={jaDate(store.created_at)} small />
        </div>
      </Card>

      <Card
        title={`所属ユーザー（${members.length}）`}
          actions={
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => setAddingMember((open) => !open)}
            >
              {addingMember ? '閉じる' : '＋ ユーザーを追加'}
            </button>
          }
        >
          {/* Store screen: the scope is this store, so neither the role nor the
              store is a choice the operator has to make again. */}
          <AddMember
            clinicId={store.clinic_id}
            storeId={store.id}
            open={addingMember}
            onClose={() => setAddingMember(false)}
          />

          {members.length === 0 ? (
            <Empty>
              この店舗に割り当てられたユーザーはいません。クリニック管理者は全店舗を操作できます。
            </Empty>
          ) : (
            <Table
              head={
                <>
                  <th scope="col">氏名</th>
                  <th scope="col">状態</th>
                  <th scope="col">最終ログイン</th>
                  <th scope="col" />
                </>
              }
            >
              {members.map((member) => (
                <tr key={member.id} className="row-link">
                  <td>
                    {/* The person, on the person's own page. */}
                    <RowLink to={`/admin/users/${member.id}`}>
                      {member.display_name || member.email || '—'}
                    </RowLink>
                    <span className="muted small" style={{ display: 'block' }}>
                      {member.email}
                    </span>
                  </td>
                  <td>
                    <span className="chips">
                      <Pill tone={member.role === 'STORE_ADMIN' ? 'default' : 'muted'}>
                        {ROLE_LABEL[member.role] ?? member.role}
                      </Pill>
                      <StatusPill status={member.status} />
                    </span>
                  </td>
                  <td>{jaDate(member.last_login_at)}</td>
                  <td>
                    {/*
                      The same editor the clinic screen uses, so somebody can be
                      moved to another branch or promoted from here - the store
                      is where an operator notices the assignment is wrong, and
                      sending them to the clinic screen to fix it is how a small
                      correction becomes a task for later.

                      `stores` is every store of this clinic, not just this one:
                      the point of opening it here is usually to move somebody
                      somewhere else.
                    */}
                    <MemberEditor
                      clinicId={store.clinic_id}
                      member={{
                        id: member.id,
                        email: member.email,
                        display_name: member.display_name,
                        clinic_admin: false,
                        role: member.role,
                        stores: [{ id: store.id, name: store.name }],
                      }}
                      stores={siblingStores.items}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          )}
      </Card>

      <Card
        title="この店舗の登録内容"
        caption="「クリニックから継承」は、この店舗で上書きしていない項目です。すべて「編集」から変更できます。"
      >
        {/*
          Everything the store has, in one grid - the same treatment the clinic
          page got, and for the same reason. Six values were listed and the rest
          (postal code, phone, closed days, the reservation and map links) were
          visible only by opening the edit panel, which is backwards: those are
          the values an article is allowed to state, so they are exactly what an
          operator comes here to check.

          Inheritance stays visible. "empty" and "inherited from the clinic"
          look identical otherwise, and the difference decides what the
          generator may write.
        */}
        <dl className="factgrid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
          <StoreFact label="サイトURL">
            <Inherited own={store.site_url} inherited={store.clinic_site_url} />
          </StoreFact>
          <StoreFact label="文体・トーン">
            <Inherited own={store.default_tone} inherited={store.clinic_default_tone} />
          </StoreFact>
          <StoreFact label="WordPressカテゴリ">
            <Inherited own={store.wordpress_category} inherited={store.clinic_wordpress_category} />
          </StoreFact>
          <StoreFact label="対策キーワード">
            <InheritedKeywords
              own={store.default_keywords}
              inherited={store.clinic_default_keywords}
            />
          </StoreFact>
          <StoreFact label="郵便番号">{store.postal_code}</StoreFact>
          <StoreFact label="所在地">
            {[store.prefecture, store.city, store.address].filter(Boolean).join(' ')}
          </StoreFact>
          <StoreFact label="アクセス">{store.access_note}</StoreFact>
          <StoreFact label="電話番号">{store.phone}</StoreFact>
          <StoreFact label="診療時間">{store.hours_note}</StoreFact>
          <StoreFact label="休診日">{store.closed_note}</StoreFact>
          <StoreFact label="予約URL">
            {store.reservation_url ? (
              <a href={store.reservation_url} target="_blank" rel="noopener noreferrer">
                {store.reservation_url}
              </a>
            ) : null}
          </StoreFact>
          <StoreFact label="地図URL">
            {store.map_url ? (
              <a href={store.map_url} target="_blank" rel="noopener noreferrer">
                {store.map_url}
              </a>
            ) : null}
          </StoreFact>
        </dl>
      </Card>

      <Card
        title="この店舗の承認済み情報"
        caption="AIが記事に書けるのは、ここで承認済みになっている事実だけです。"
      >
        {totalFacts === 0 ? (
          <Empty>
            この店舗専用の登録はありません。クリニック共通として登録された情報は利用できます。
          </Empty>
        ) : (
          <div className="grid grid--4">
            {facts.map((fact) => (
              <Stat
                key={fact.kind}
                label={FACT_LABEL[fact.kind] ?? fact.kind}
                value={jaNumber(fact.approved)}
                hint={
                  Number(fact.total) > Number(fact.approved) ? (
                    <Pill tone="warn">未承認 {Number(fact.total) - Number(fact.approved)}</Pill>
                  ) : (
                    <Pill tone="ok">すべて承認済み</Pill>
                  )
                }
              />
            ))}
          </div>
        )}
      </Card>

      {/*
        The rows behind the counts above, narrowed to this store: its own
        material plus the clinic-wide rows it inherits. Editable here because an
        operator looking at 「未承認 1」 wants to approve that one, not go and
        find the clinic screen.
      */}
      <ClinicFacts
        clinicId={store.clinic_id}
        data={material}
        stores={[{ id: store.id, name: store.name }]}
        storeId={store.id}
      />

      <ThemePicker
        endpoint={`/admin/stores/${store.id}/theme`}
        value={store.brand_color ?? null}
        inherited={{ label: 'クリニックの色', hex: store.clinic_brand_color ?? null }}
      />

      <Card title="この店舗に関する操作履歴">
        {activity.length === 0 ? (
          <Empty>記録された操作はまだありません。</Empty>
        ) : (
          <Table
            head={
              <>
                <th scope="col">操作</th>
                <th scope="col">結果</th>
                <th scope="col">日時</th>
              </>
            }
          >
            {activity.map((entry) => (
              <tr key={entry.id}>
                <td>{entry.action}</td>
                <td>
                  <Pill tone={entry.outcome === 'DENIED' ? 'danger' : 'ok'}>{entry.outcome}</Pill>
                </td>
                <td>{new Date(entry.created_at).toLocaleString('ja-JP')}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="店舗の削除" caption="取り消せません。この店舗のデータが消えます。">
        <DangerButton
          label="この店舗を削除する"
          confirmText={store.name}
          consequences={[
            'この店舗の記事・下書き・画像',
            'この店舗にだけ登録された施術・強み・監修医・CTA・ペルソナ',
            'この店舗の会員割り当て',
          ]}
          onConfirm={() => del(`/admin/stores/${store.id}`)}
          onDone={() => void navigate(`/admin/clinics/${store.clinic_id}`)}
        />
        <p className="muted small" style={{ marginBottom: 0 }}>
          クリニック共通として登録された情報は残ります。消えるのはこの店舗のものだけです。
        </p>
      </Card>
    </>
  );
}
